import type { NotificationLocationHierarchy } from "../Domain/NotificationSettings.types";
import type { ServiceHierarchyNode } from "../Domain/serviceNotificationHierarchy.types";

export function leafIdForBookableInventory(inventoryId: number): string {
  return `inv-${inventoryId}`;
}

export function parseBookableInventoryIdFromLeafId(leafId: string): number | null {
  const match = /^inv-(\d+)$/.exec(leafId);
  return match ? Number(match[1]) : null;
}

/**
 * Builds the virtual-root tree for the approval-admin scope UI from API flat lists.
 * Returns null when there is nothing to show.
 */
export function buildApprovalHierarchyRootFromRemote(
  hierarchy: NotificationLocationHierarchy | undefined
): ServiceHierarchyNode | null {
  if (!hierarchy?.locations?.length) {
    return null;
  }

  const { locations, floors, rooms } = hierarchy;

  const locationNodes: ServiceHierarchyNode[] = locations.flatMap(loc => {
    const locFloors = floors.filter(f => f.locationInventoryId === loc.inventoryId);
    const floorNodes: ServiceHierarchyNode[] = locFloors.flatMap(floor => {
      const floorRooms = rooms.filter(
        r => r.floorInventoryId === floor.inventoryId && r.locationInventoryId === loc.inventoryId
      );
      const seen = new Set<number>();
      const leafNodes: ServiceHierarchyNode[] = [];
      for (const r of floorRooms) {
        if (seen.has(r.inventoryId)) {
          continue;
        }
        seen.add(r.inventoryId);
        leafNodes.push({
          id: leafIdForBookableInventory(r.inventoryId),
          labelLiteral: r.name
        });
      }
      if (leafNodes.length === 0) {
        return [];
      }
      const floorNode: ServiceHierarchyNode = {
        id: `floor-${floor.inventoryId}`,
        labelLiteral: floor.name,
        appendAllSuffix: true,
        children: leafNodes
      };
      return [floorNode];
    });

    if (floorNodes.length === 0) {
      return [];
    }

    const locationNode: ServiceHierarchyNode = {
      id: `loc-${loc.inventoryId}`,
      labelLiteral: loc.name,
      ...(loc.subtitle?.trim() ? { subtitleLiteral: loc.subtitle.trim() } : {}),
      appendAllSuffix: true,
      children: floorNodes
    };
    return [locationNode];
  });

  if (locationNodes.length === 0) {
    return null;
  }

  return {
    id: "approval-hierarchy-root",
    labelKey: "_serviceNotificationHierarchy_allLocations",
    children: locationNodes
  };
}

export function buildInitialEnabledMapForApprovalLeaves(
  leafIds: readonly string[],
  scope: number[] | null | undefined
): Record<string, boolean> {
  const scopeSet = scope?.length ? new Set(scope) : null;
  return Object.fromEntries(
    leafIds.map(id => {
      const num = parseBookableInventoryIdFromLeafId(id);
      const on = scopeSet === null ? true : num !== null && scopeSet.has(num);
      return [id, on];
    })
  );
}

export function buildAllLeavesEnabledMap(leafIds: readonly string[]): Record<string, boolean> {
  return Object.fromEntries(leafIds.map(id => [id, true]));
}
